import { Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { useCreateDataItem } from '~/api/data-items';
import { Dialog, DialogForm, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import type { DataSourceData__DataSource as DataSource } from '../../queries/data.generated';

interface AddRowDialogProps {
  dataSource: DataSource;
  onClose: () => void;
  open: boolean;
}

export const AddRowDialog = ({ dataSource, onClose, open }: AddRowDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createDataItem] = useCreateDataItem();

  const requiredField = dataSource.foreignIdName;
  const closeAfterSubmitRef = useRef(true);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [addedCount, setAddedCount] = useState(0);
  const [lastAddedId, setLastAddedId] = useState<number | null>(null);

  const validationSchema = useMemo(
    () =>
      object({
        ...(requiredField && {
          [requiredField]: string()
            .trim()
            .required(t('pages.data_sources.add_row_dialog.validation.foreign_id_required')),
        }),
      }),
    [requiredField, t],
  );

  const formik = useFormik({
    initialValues: Object.fromEntries(dataSource.keys.map((key) => [key, ''])),
    validationSchema,
    validateOnBlur: true,
    onSubmit: async (values, { resetForm }) => {
      const response = await createDataItem({
        variables: {
          input: {
            dataSourceId: dataSource.id,
            data: values,
          },
        },
      });

      const newItemId = response.data?.createDataItem?.dataItem.id;
      if (newItemId) {
        resetForm();
        if (closeAfterSubmitRef.current) {
          setAddedCount(0);
          setLastAddedId(null);
          onClose();
          navigate(`#${newItemId}`);
        } else {
          setAddedCount((c) => c + 1);
          setLastAddedId(newItemId);
          firstInputRef.current?.focus();
        }
        closeAfterSubmitRef.current = true;
      }
    },
  });

  const handleAddAnother = () => {
    closeAfterSubmitRef.current = false;
    formik.handleSubmit();
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
    if (lastAddedId) {
      navigate(`#${lastAddedId}`);
    }
    setAddedCount(0);
    setLastAddedId(null);
  };

  const canSubmit = !requiredField || !!formik.values[requiredField].trim();

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>
        {t('pages.data_sources.add_row_dialog.title')}
      </DialogTitle>

      <DialogContent>
        <DialogForm id="add-row-form" onSubmit={formik.handleSubmit}>
          {dataSource.keys.map((key, index) => {
            const isRequired = key === requiredField;
            const error = formik.touched[key] && formik.errors[key];
            return (
              <label key={key}>
                <span className={`label${isRequired ? ' required' : ''}`}>{key}</span>
                <TextField
                  autoFocus={index === 0}
                  error={!!error}
                  fullWidth
                  helperText={error || undefined}
                  inputRef={index === 0 ? firstInputRef : undefined}
                  name={key}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values[key]}
                />
              </label>
            );
          })}
        </DialogForm>
      </DialogContent>

      <DialogActions>
        {addedCount > 0 && (
          <Typography sx={{ mr: 'auto' }} variant="body1">
            {t('pages.data_sources.add_row_dialog.added_count', { count: addedCount })}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={formik.isSubmitting} onClick={handleClose} variant="outlined">
            {t('common.buttons.cancel')}
          </Button>
          <LoadingButton
            disabled={formik.isSubmitting || !canSubmit}
            loading={formik.isSubmitting}
            onClick={handleAddAnother}
            variant="outlined"
          >
            {t('pages.data_sources.add_row_dialog.add_another')}
          </LoadingButton>
          <LoadingButton
            color="primary"
            disabled={formik.isSubmitting || !canSubmit}
            form="add-row-form"
            loading={formik.isSubmitting}
            loadingPosition="start"
            startIcon={<Add />}
            type="submit"
            variant="contained"
          >
            {t('pages.data_sources.add_row_dialog.add')}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
